// src/services/api.js
import axios from 'axios';
import { isSessionExpiry } from './lib/sessionExpiry';
import { transactionServiceExtension } from './transactionServiceExtension.js';
import host from '../host';
import { resolveApiUrl } from './apiBaseUrl';
import { printedAtStamp } from '../utils/printedAtStamp';

const API_URL = resolveApiUrl({
    hostType: host.type,
    backendEnv: localStorage.getItem('aqana.backendEnv'),
    envVar: process.env.REACT_APP_API_URL,
    hostname: window.location.hostname,
});

console.log('Initial API_URL:', process.env.REACT_APP_API_URL || 'http://localhost:8000');
console.log('Final API_URL after processing:', API_URL);

// Flag to use mock data instead of real API calls (for development)
// Create axios instance with base URL
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Apply the HTTPS interceptor to the global axios
api.interceptors.request.use(config => {
    // Upgrade baseURL from HTTP to HTTPS for non-localhost (catches bad env var values)
    if (config.baseURL && config.baseURL.startsWith('http://') &&
        !config.baseURL.includes('localhost') && !config.baseURL.includes('127.0.0.1')) {
        config.baseURL = config.baseURL.replace('http://', 'https://');
    }
    // Upgrade absolute URL in config.url from HTTP to HTTPS for non-localhost
    if (config.url && config.url.startsWith('http://') &&
        !config.url.includes('localhost') && !config.url.includes('127.0.0.1')) {
        config.url = config.url.replace('http://', 'https://');
    }

    return config;
}, error => {
    return Promise.reject(error);
});
// Apply the token interceptor to your api instance
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        // Include sync token for device-level auth (Electron sync)
        const syncToken = localStorage.getItem('sync_token');
        if (syncToken) {
            config.headers['X-Sync-Token'] = syncToken;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Offline handling interceptor
api.interceptors.response.use(
    response => response,
    async error => {
        // Check for device revocation or token expiry (401 with specific detail)
        if (error.response && error.response.status === 401) {
            const detail = error.response.data?.detail;
            if (detail === 'sync_token_revoked') {
                window.dispatchEvent(new CustomEvent('device-revoked'));
                return Promise.reject(error);
            }
            if (detail === 'sync_token_expired') {
                window.dispatchEvent(new CustomEvent('device-expired'));
                return Promise.reject(error);
            }

            // A plain 401 means the USER's JWT is no longer valid — almost
            // always because it expired (ACCESS_TOKEN_EXPIRE_MINUTES is 24h,
            // backend/app/services/auth.py:26), which the backend reports as
            // "Could not validate credentials".
            //
            // This used to fall straight through to the generic reject, so
            // every caller rendered its own failure copy. On the Manager
            // Portal that reads "Couldn't load your overview — check your
            // connection and try again", which is actively wrong: the network
            // is fine, the session is dead, and Retry can never succeed. A
            // pilot tenant sat on that screen for a day (reported 2026-08-27)
            // because nothing told them to sign in again.
            //
            // Surfacing it as a session expiry lets AuthContext end the
            // session and return to login, which is the only action that
            // actually resolves it.
            if (isSessionExpiry(error.config?.url, !!localStorage.getItem('token'))) {
                window.dispatchEvent(new CustomEvent('session-expired'));
                return Promise.reject(error);
            }
        }

        return Promise.reject(error);
    }
);

// Authentication services
export const authService = {
    tenantLogin: async (tenantUsername, password, shortId = null) => {
        try {
            console.log(' API: Attempting login for:', tenantUsername);
            const body = { username: tenantUsername, password };
            if (shortId !== null && shortId !== undefined && shortId !== '') {
                body.short_id = parseInt(shortId, 10);
            }
            const response = await api.post('/auth/tenant-login', body);

            console.log(' API: Full login response:', response.data);
            console.log(' API: user_role from response:', response.data.user_role);

            if (response.data.access_token) {
                localStorage.setItem('token', response.data.access_token);
                localStorage.setItem('user_role', response.data.user_role || '');
                localStorage.setItem('tenant_id', response.data.tenant_id || '');
                localStorage.setItem('company_id', response.data.company_id || '');
                localStorage.setItem('role_name', response.data.role_name || '');
                localStorage.setItem('display_name', response.data.display_name || '');
                localStorage.setItem('auth_type', 'tenant');

                console.log(' API: Stored in localStorage:');
                console.log('  - user_role:', localStorage.getItem('user_role'));
                console.log('  - tenant_id:', localStorage.getItem('tenant_id'));
                console.log('  - company_id:', localStorage.getItem('company_id'));
            }
            return response.data;
        } catch (error) {
            console.error(' API: Login error:', error);
            throw error;
        }
    },

    employeeLogin: async (tenantId, pin) => {
        try {
            const response = await api.post('/auth/employee-login', {
                tenant_id: tenantId,
                pin
            });
            if (response.data.access_token) {
                localStorage.setItem('token', response.data.access_token);
                localStorage.setItem('user_role', response.data.user_role || '');
                localStorage.setItem('employee_name', response.data.employee_name || '');
                localStorage.setItem('employee_id', response.data.employee_id || '');
                localStorage.setItem('auth_type', 'employee');
            }
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Keep existing login method for backward compatibility
    login: async (username, password) => {
        return authService.tenantLogin(username, password);
    },

    logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('access_token');
        localStorage.removeItem('user_role');
        localStorage.removeItem('username');
        localStorage.removeItem('employee_name');
        localStorage.removeItem('employee_id');
        localStorage.removeItem('auth_type');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('authType');
        localStorage.removeItem('role_name');
        // tenant_id, locked_tenant_id, locked_tenant_name are intentionally NOT
        // removed here — AuthContext.logout() preserves them across sessions.
    },
    isAuthenticated: () => {
        return !!localStorage.getItem('token');
    },
    getUserRole: () => {
        return localStorage.getItem('user_role') || '';
    },
    superAdminLogin: async (username, password) => {
        try {
            const response = await api.post('/auth/super-admin-login', {
                username,
                password
            });
            if (response.data.access_token) {
                localStorage.setItem('token', response.data.access_token);
                localStorage.setItem('access_token', response.data.access_token);
                localStorage.setItem('user_role', 'super_admin');
            }
            return response.data;
        } catch (error) {
            throw error;
        }
    },
};

// Product services
export const productService = {
    getAllProducts: async (includeInactive = false) => {
        try {
            const params = includeInactive ? '?include_inactive=true' : '';
            const response = await api.get(`/products/${params}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getProduct: async (productCode) => {
        try {
            const response = await api.get(`/products/${productCode}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getAddonUsageMap: async () => {
        try {
            const response = await api.get('/products/addons/usage-map');
            return response.data;
        } catch (error) {
            return {};
        }
    },

    assignAddons: async (productCode, addonProductCodes) => {
        const response = await api.put(`/products/${productCode}/addons`, {
            addon_product_codes: addonProductCodes,
        });
        return response.data;
    },

    getProductsByCategory: async (category, includeInactive = false) => {
        try {
            const params = includeInactive ? '?include_inactive=true' : '';
            const response = await api.get(`/products/category/${category}${params}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getSubcategoriesByCategory: async (category) => {
        try {
            const response = await api.get(`/products/subcategories/${category}`);
            return response.data;
        } catch (error) {
            console.error('Failed to load subcategories:', error);
            return [];
        }
    },

    createProduct: async (productData) => {
        try {
            const response = await api.post('/products/', productData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    updateProduct: async (productId, productData) => {
        try {
            const response = await api.put(`/products/${productId}`, productData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    deactivateProduct: async (productCode, reason = null) => {
        const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
        const response = await api.put(`/products/${productCode}/deactivate${params}`);
        return response.data;
    },

    reactivateProduct: async (productCode) => {
        const response = await api.put(`/products/${productCode}/reactivate`);
        return response.data;
    },

    deleteProduct: (productCode, reason = null) => {
        return productService.deactivateProduct(productCode, reason);
    },

    bulkUpdateStatus: async (productCodes, isActive) => {
        const response = await api.patch('/products/bulk-status', {
            product_codes: productCodes,
            is_active: isActive,
        });
        return response.data;
    },

    uploadEntityImage: async (entityType, entityId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post(
            `/products/image-upload?entity_type=${entityType}&entity_id=${encodeURIComponent(entityId)}`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        return response.data;
    },
};

// Inventory services
export const inventoryService = {
    // Get all inventory items
    getAllInventory: async () => {
        try {
            const response = await api.get('/inventory/');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Create new inventory item
    createInventoryItem: async (inventoryData) => {
        try {
            const response = await api.post('/inventory/', inventoryData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Update inventory item
    updateInventoryItem: async (inventoryId, inventoryData) => {
        try {
            const response = await api.put(`/inventory/${inventoryId}`, inventoryData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Backdated cost correction (cloud-only — no local DB fallback).
    // Only sends cost, effective_from (past date), reason — server enforces past-date rule.
    correctInventoryCost: async (inventoryId, payload) => {
        const response = await api.patch(`/inventory/${inventoryId}/cost-correction`, payload);
        return response.data;
    },

    // Delete inventory item
    deleteInventoryItem: async (inventoryId) => {
        try {
            const response = await api.delete(`/inventory/${inventoryId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Create inventory transaction (purchase, usage, adjustment)
    createInventoryTransaction: async (transactionData) => {
        try {
            const response = await api.post('/inventory/transactions', transactionData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Get transactions for specific inventory item
    getInventoryTransactions: async (inventoryId, dateFrom, dateTo) => {
        try {
            const params = {};
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            const response = await api.get(`/inventory/transactions/${inventoryId}`, { params });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Set ingredients for a product
    setProductIngredients: async (productCode, ingredients) => {
        try {
            const response = await api.post(`/inventory/product-ingredients/${productCode}`, ingredients);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Get ingredients for a product
    getProductIngredients: async (productCode) => {
        try {
            const response = await api.get(`/inventory/product-ingredients/${productCode}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    calculateProductStock: async (productCode, diningOption = 'both') => {
        try {
            const response = await api.get(`/inventory/calculate-stock/${productCode}?dining_option=${diningOption}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Add after the calculateProductStock method
    deductStockForStaffAllowance: async (deductionData) => {
        try {
            const response = await api.post('/inventory/deduct-for-staff-allowance', deductionData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Add bulk stock calculation method
    calculateBulkProductStock: async (productCodes) => {
        try {
            const response = await api.post('/inventory/calculate-bulk-stock', { product_codes: productCodes });
            return response.data;
        } catch (error) {
            console.error('Failed to calculate bulk stock:', error);
            throw error;
        }
    },
};

// Employee services
export const employeeService = {
    getAllEmployees: async () => {
        try {
            const response = await api.get('/employees/');
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    getActiveCashiers: async () => {
        try {
            const response = await api.get('/employees/active/cashiers');
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    createEmployee: async (employeeData) => {
        try {
            const response = await api.post('/employees/', employeeData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    updateEmployee: async (employeeId, employeeData) => {
        try {
            const response = await api.put(`/employees/${employeeId}`, employeeData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    deactivateEmployee: async (employeeId, reason = null) => {
        const params = reason ? { reason } : {};
        return await api.put(`/employees/${employeeId}/deactivate`, {}, { params });
    },

    deleteStaffAllowance: async (employeeId, staffAllowanceId, reason = null) => {
        const params = reason ? { reason } : {};
        return await api.delete(`/employees/allowances/${employeeId}/${staffAllowanceId}`, { params });
    },

    getEmployeeSalary: async (employeeId) => {
        try {
            const response = await api.get(`/employees/${employeeId}/salary`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    updateEmployeeSalary: async (employeeId, newSalary) => {
        try {
            const response = await api.put(`/employees/${employeeId}/salary`, { salary: newSalary });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getEmployeeAllowances: async (employeeId) => {
        try {
            const response = await api.get(`/employees/allowances/${employeeId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    createEmployeeAllowance: async (allowanceData) => {
        try {
            console.log("Sending allowance data to API:", allowanceData);
            const response = await api.post('/employees/allowances', allowanceData);
            console.log("API response for allowance:", response.data);
            return response.data;
        } catch (error) {
            console.error("API error creating allowance:", error.response?.data || error.message);
            throw error;
        }
    },
    getEmployeeAdvances: async (employeeId) => {
        try {
            const response = await api.get(`/employees/${employeeId}/advances`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    createSalaryAdvance: async (advanceData) => {
        try {
            const response = await api.post('/employees/advances', advanceData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    deleteEmployeeAdvance: async (employeeId, expenseId) => {
        const response = await api.delete(`/employees/advances/${employeeId}/${expenseId}`);
        return response.data;
    },
    getSalaryPayments: async (employeeId) => {
        try {
            const response = await api.get(`/employees/${employeeId}/payments`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    createSalaryPayment: async (paymentData) => {
        try {
            const response = await api.post('/employees/payments', paymentData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    deleteSalaryPayment: async (employeeId, expenseId) => {
        return await api.delete(`/employees/payments/${employeeId}/${expenseId}`);
    },

};

// Shift services
export const shiftService = {
    getAllShifts: async () => {
        try {
            const response = await api.get('/shifts/all');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getShiftsByDateRange: async (startDate, endDate) => {
        try {
            const response = await api.get(`/shifts/date-range?start_date=${startDate}&end_date=${endDate}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getShiftsByDate: async (date) => {
        try {
            const response = await api.get(`/shifts/sales-date/${encodeURIComponent(date)}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getShiftsByWeek: async (weekNumber) => {
        try {
            const response = await api.get(`/shifts/week/${weekNumber}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    createShift: async (shiftData) => {
        try {
            const response = await api.post('/shifts/', shiftData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    updateShift: async (shiftId, shiftData) => {
        try {
            const response = await api.put(`/shifts/${shiftId}`, shiftData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    patchStation: async (shiftId, station) => {
        const response = await api.patch(`/shifts/${shiftId}/station`, { station });
        return response.data;
    },

    deleteShift: async (shiftId) => {
        try {
            const response = await api.delete(`/shifts/${shiftId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    clockIn: async (pin) => {
        const response = await api.post('/shifts/clock-in', { pin });
        return response.data;
    },

    clockOut: async (pin) => {
        const response = await api.post('/shifts/clock-out', { pin });
        return response.data;
    },

    managerClockIn: async (shiftId, timeOverride) => {
        const params = timeOverride ? `?time_override=${timeOverride}` : '';
        const response = await api.post(`/shifts/${shiftId}/clock-in${params}`);
        return response.data;
    },

    managerClockOut: async (shiftId, timeOverride) => {
        const params = timeOverride ? `?time_override=${timeOverride}` : '';
        const response = await api.post(`/shifts/${shiftId}/clock-out${params}`);
        return response.data;
    },

    copyWeek: async (sourceWeek, targetWeek, sourceYear, targetYear) => {
        const response = await api.post('/shifts/copy-week', {
            source_week: sourceWeek,
            target_week: targetWeek,
            source_year: sourceYear,
            target_year: targetYear,
        });
        return response.data;
    },

    getEmployeeHours: async (employeeId, start, end) => {
        const response = await api.get(`/shifts/employee/${employeeId}/hours?start=${start}&end=${end}`);
        return response.data;
    },

    getCoverage: async (weekNumber, year) => {
        const params = year ? `?year=${year}` : '';
        const response = await api.get(`/shifts/coverage/${weekNumber}${params}`);
        return response.data;
    },
};

export const stationPreferenceService = {
    get: async (employeeId) => {
        const response = await api.get(`/employees/v2/${employeeId}/station-preferences`);
        return response.data;
    },

    set: async (employeeId, data) => {
        const response = await api.post(`/employees/v2/${employeeId}/station-preferences`, data);
        return response.data;
    },
};

// Addon services
export const addonService = {
    getAllAddons: async () => {
        try {
            // Use the debug endpoint temporarily
            const response = await api.get('/products/addons-debug');
            return response.data;
        } catch (error) {
            console.error('Error fetching addons:', error);
            return [];
        }
    },

    getAddonsByCategory: async (category) => {
        try {
            const response = await api.get(`/products/addons/category/${category}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    createAddon: async (addonData) => {
        try {
            // Use the debug endpoint temporarily
            const response = await api.post('/products/addons-debug', addonData);
            return response.data;
        } catch (error) {
            console.error('Error creating addon:', error);
            throw error;
        }
    },

    updateAddon: async (addonId, addonData) => {
        try {
            const response = await api.put(`/products/addons/${addonId}`, addonData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    deleteAddon: async (addonId) => {
        try {
            const response = await api.delete(`/products/addons/${addonId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }
};

// Modifier group services (nested add-ons: reusable option groups, optionally
// nested onto an add-on so choosing that add-on opens a further choice).
export const modifierService = {
    getGroups: () => api.get('/modifiers/groups').then(r => r.data),
    createGroup: (payload) => api.post('/modifiers/groups', payload).then(r => r.data),
    updateGroup: (groupId, payload) =>
        api.put(`/modifiers/groups/${groupId}`, payload).then(r => r.data),
    deleteGroup: (groupId) => api.delete(`/modifiers/groups/${groupId}`).then(r => r.data),
    setGroupOptions: (groupId, options) =>
        api.put(`/modifiers/groups/${groupId}/options`, { options }).then(r => r.data),
    getProductGroups: (productCode) =>
        api.get(`/modifiers/products/${productCode}/groups`).then(r => r.data),
    setProductGroups: (productCode, groupIds) =>
        api.put(`/modifiers/products/${productCode}/groups`, { group_ids: groupIds }).then(r => r.data),
    setAddonGroups: (addonCode, groupIds) =>
        api.put(`/modifiers/addons/${addonCode}/groups`, { group_ids: groupIds }).then(r => r.data),
    // Always hits the API directly, so every POS surface can call this
    // unconditionally on mount without re-deriving its own connectivity check.
    getCatalog: async () => {
        try {
            const response = await api.get('/modifiers/catalog');
            return response.data;
        } catch (error) {
            throw error;
        }
    },
};

// Old category services removed — replaced by new categoryService below (Catalogue CRUD)

export const transactionService = {
    createSimplifiedEndOfDayReport: transactionServiceExtension.createSimplifiedEndOfDayReport,

    createTransaction: async (transactionData) => {
        try {
            const response = await api.post('/transactions/', transactionData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // How a completed split was actually paid. The parent row exists before
    // the mix is known — its children are created as each group pays, so
    // their receipts have something to render from — and this closes the
    // loop once the last group has settled.
    setSplitTotals: async (transactionId, { cashAmount = 0, cardAmount = 0 }) => {
        const response = await api.patch(`/transactions/${encodeURIComponent(transactionId)}/split-totals`, {
            cash_amount: cashAmount,
            card_amount: cardAmount,
        });
        return response.data;
    },

    deleteTransaction: async (transactionId, reason = null, deletedBy = null) => {
        const qp = new URLSearchParams();
        if (reason) qp.set('reason', reason);
        if (deletedBy) qp.set('deleted_by', deletedBy);
        const qs = qp.toString() ? `?${qp.toString()}` : '';
        return await api.delete(`/transactions/${transactionId}${qs}`);
    },

    archiveBusinessDay: async (salesDate) => {
        try {
            const response = await api.post(`/end-of-day/archive-day/${encodeURIComponent(salesDate)}/`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getTransactionsAfterStartOfDay: async (salesDate) => {
        try {
            // Encoded, not interpolated raw: a session's salesDate is a full
            // timestamp ("2026-08-27 10:37:42"), and on the native POS a raw
            // space in the path reaches the backend as a literal `+` — see
            // salesDatePathEncoding.test.js. The day's sales then match
            // nothing and the till reports an empty business day.
            const response = await api.get(`/transactions/sales-date/${encodeURIComponent(salesDate)}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getTransactionsRange: async ({ startDate, endDate, limit = 25, offset = 0, staff, paymentMethod, diningOption, search, sortField = 'time', sortDir = 'desc' }) => {
        const params = { start_date: startDate, end_date: endDate, limit, offset, sort_field: sortField, sort_dir: sortDir };
        if (staff) params.staff = staff;
        if (paymentMethod) params.payment_method = paymentMethod;
        if (diningOption) params.dining_option = diningOption;
        if (search) params.search = search;
        const response = await api.get('/transactions/range', { params });
        return response.data;
    },

    cancelOrderPrint: async (orderKey) => {
        try {
            // Encoded — an order key is `${diningOption}-${tableNumber}`, so
            // it carries a space from a multi-word dining option and a `#`
            // from a no-table order number. See orderKeyPathEncoding.test.js.
            const response = await api.post(
                `/transactions/cancel-print/${encodeURIComponent(orderKey)}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    ...transactionServiceExtension
};

// Expense services
export const expenseService = {
    createExpense: async (expenseData) => {
        try {
            const response = await api.post('/expenses/', expenseData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    updateExpense: async (expenseId, expenseData) => {
        try {
            const response = await api.put(`/expenses/${expenseId}`, expenseData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    deleteExpense: async (expenseId, reason = null) => {
        const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
        return await api.delete(`/expenses/${expenseId}${params}`);
    },

    getExpensesAfterStartOfDay: async (salesDate) => {
        try {
            const response = await api.get(`/expenses/sales-date/${encodeURIComponent(salesDate)}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    scanReceipt: async (formData) => {
        try {
            const response = await api.post('/expenses/scan-receipt', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getOverheads: async (days = 30) => {
        try {
            const response = await api.get(`/expenses/overheads?days=${days}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    getOneOffExpenses: async (year, month) => {
        const response = await api.get(`/expenses/one-off/${year}/${month}`);
        return response.data;
    },
    getActiveBusinessDays: async (year, month) => {
        const response = await api.get(`/expenses/active-business-days/${year}/${month}`);
        return response.data;
    },
};

// Order Tracking services
export const orderTrackingService = {
    createOrderTracking: async (orderData) => {
        try {
            const response = await api.post('/orders/tracking', orderData);
            return response.data;
        } catch (error) {
            console.error('Error creating order tracking:', error);
            throw error;
        }
    },

    updateOrderTracking: async (trackingId, updateData) => {
        try {
            const response = await api.put(`/orders/tracking/${trackingId}`, updateData);
            return response.data;
        } catch (error) {
            console.error('Error updating order tracking:', error);
            throw error;
        }
    },

    markOrderEdited: async (orderId) => {
        try {
            const response = await api.put(`/orders/tracking/mark-edited/${orderId}`);
            return response.data;
        } catch (error) {
            console.error('Error marking order as edited:', error);
        }
    },

    updateTrackingByOrderId: async (orderId, transactionId) => {
        try {
            const response = await api.put(`/orders/tracking/update-by-order/${orderId}?transaction_id=${transactionId}`);
            return response.data;
        } catch (error) {
            console.error('Error updating tracking by order ID:', error);
            throw error;
        }
    },

    updateOrderStatus: async (trackingId, status, notes = null) => {
        try {
            const response = await api.put(`/orders/tracking/${trackingId}/status`, {
                status,
                notes
            });
            return response.data;
        } catch (error) {
            console.error('Error updating order status:', error);
            throw error;
        }
    },

    // Add this method to the orderTrackingService:
    deletePendingOrder: (orderKey, reason = null, orderSnapshot = null, deletedBy = null) => {
        const qp = new URLSearchParams();
        if (reason) qp.set('reason', reason);
        if (deletedBy) qp.set('deleted_by', deletedBy);
        const qs = qp.toString() ? `?${qp.toString()}` : '';
        // Encoded — see cancelOrderPrint above. Unencoded, a no-table key
        // ("Takeaway-#001") truncates at the `#`, taking the query string
        // with it, so the void lands on the wrong order with no reason.
        return api.delete(`/orders/pending/${encodeURIComponent(orderKey)}${qs}`, {
            data: orderSnapshot || null,
        });
    },

    deleteOrderTrackingByOrder: (orderId) =>
        api.delete(`/orders/tracking/order/${orderId}`),

    getOrdersByCategory: async (category, status = null) => {
        try {
            const params = status && status !== 'all' ? { status } : {};
            const response = await api.get(`/orders/category/${category}`, { params });
            return response.data;
        } catch (error) {
            console.error('Error getting orders by category:', error);
            throw error;
        }
    },

    getCompletedOrdersByCategory: async (category, salesDate = null) => {
        try {
            const params = salesDate ? { sales_date: salesDate } : {};
            const response = await api.get(`/orders/completed/${category}`, { params });
            return response.data;
        } catch (error) {
            console.error('Error getting completed orders:', error);
            throw error;
        }
    },

    getTimingAnalytics: async (category) => {
        try {
            const response = await api.get(`/orders/analytics/timing?category=${category}&days=7`);
            return response.data;
        } catch (error) {
            console.error('Error getting timing analytics:', error);
            return null;
        }
    },

    getOrderCount: async (category) => {
        try {
            const response = await api.get(`/orders/category/${category}/count`);
            return response.data;
        } catch (error) {
            console.error('Error getting order count:', error);
            // Fallback to current method if count endpoint doesn't exist yet
            try {
                const orders = await api.get(`/orders/category/${category}`);
                return {
                    count: orders.data.length,
                    latest_timestamp: orders.data[0]?.submitted_at || null
                };
            } catch (fallbackError) {
                return { count: 0, latest_timestamp: null };
            }
        }
    },

    deleteOrderTrackingByCategory: async (orderId, category) => {
        try {
            const response = await api.delete(`/orders/tracking/order/${orderId}/category/${category}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting order tracking by category:', error);
            throw error;
        }
    },

    // Add this method to orderTrackingService object
    getUnprintedWaiterOrders: async () => {
        try {
            const response = await api.get('/orders/unprintable-waiter-orders');
            return response.data;
        } catch (error) {
            console.error('Error getting unprinted waiter orders:', error);
            return [];
        }
    },

    markOrderCategoriesPrinted: async (trackingId) => {
        try {
            const response = await api.put(`/orders/tracking/${trackingId}/printed`, { printed: true });
            return response.data;
        } catch (error) {
            console.error('Error marking order categories as printed:', error);
            throw error;
        }
    },
};

// Tenant services
export const tenantService = {
    getTenantConfig: async () => {
        const response = await api.get('/tenants/config');
        // Cache the config for offline use
        localStorage.setItem('tenantConfig', JSON.stringify(response.data));
        return response.data;
    },

    updateTenantConfig: async (configData) => {
        const response = await api.put('/tenants/config', configData);
        return response.data;
    },

    getTenantSetting: async (settingKey, defaultValue = null) => {
        const response = await api.get(`/tenants/setting/${settingKey}?default_value=${defaultValue || ''}`);
        return response.data.value || defaultValue;
    },

    getBrandInfo: async () => {
        const response = await api.get('/tenants/brand-info');
        return response.data;
    },


    updateDesignSettings: async (designSettings) => {
        const response = await api.put('/tenants/design-settings', designSettings);
        return response.data;
    },

    updateBasicSettings: async (settings) => {
        const response = await api.put('/tenants/basic-settings', settings);
        return response.data;
    },

    // Add these methods to the tenantService object
    async getTableLayout() {
        const response = await api.get('/tenants/table-layout');
        return response.data;
    },

    async updateTableLayout(layoutData) {
        const response = await api.put('/tenants/table-layout', layoutData);
        return response.data;
    },

    getAvailableLanguages: async () => {
        const response = await api.get('/tenants/available-languages');
        return response.data;
    },

    // The tenant's own logo. Portal-only, online-only action — the config
    // value it writes is a URL that only exists once the upload has landed.
    uploadLogo: async (file) => {
        const formData = new FormData();
        formData.append('file', file, 'logo.png');
        const response = await api.post('/tenants/logo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
};

// Catalogue CRUD services
export const categoryService = {
    getAll: async () => {
        const response = await api.get('/categories/');
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/categories/', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/categories/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/categories/${id}`);
        return response.data;
    },
    getSubcategories: async (categoryId) => {
        const response = await api.get(`/categories/${categoryId}/subcategories`);
        return response.data;
    },
    createSubcategory: async (data) => {
        const response = await api.post('/categories/subcategories', data);
        return response.data;
    },
    updateSubcategory: async (id, data) => {
        const response = await api.put(`/categories/subcategories/${id}`, data);
        return response.data;
    },
    deleteSubcategory: async (id) => {
        const response = await api.delete(`/categories/subcategories/${id}`);
        return response.data;
    },
};

export const diningOptionService = {
    getAll: async () => {
        const response = await api.get('/dining-options/');
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/dining-options/', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/dining-options/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/dining-options/${id}`);
        return response.data;
    },
};

export const paymentMethodService = {
    getAll: async () => {
        const response = await api.get('/payment-methods/');
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/payment-methods/', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.put(`/payment-methods/${id}`, data);
        return response.data;
    },
    delete: async (id) => {
        const response = await api.delete(`/payment-methods/${id}`);
        return response.data;
    },
};

// Reporting services
export const reportingService = {
    getAvailableMonths: async () => {
        try {
            const response = await api.get('/reporting/analytics/available-months');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getCompanyTenants: async () => {
        try {
            const response = await api.get('/reporting/analytics/company-tenants');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getFinancialPerformance: async (month = null, tenantId = null) => {
        try {
            const params = {};
            if (month && month !== 'all-time') params.month = month;
            if (tenantId) params.tenant_id = tenantId;

            console.log(' Financial Performance API Call:');
            console.log('- URL: /reporting/financial-performance');
            console.log('- Params:', params);
            console.log('- Month:', month);
            console.log('- TenantId:', tenantId);

            const response = await api.get('/reporting/financial-performance', { params });

            console.log(' Financial Performance API Response:', response.data);
            return response.data;
        } catch (error) {
            console.error(' Financial Performance API Error:', error);
            console.error('- Status:', error.response?.status);
            console.error('- Data:', error.response?.data);
            console.error('- Message:', error.message);
            throw error;
        }
    },

    getProductIntelligence: async (month = null, tenantId = null) => {
        try {
            const params = {};
            if (month && month !== 'all-time') params.month = month;
            if (tenantId) params.tenant_id = tenantId;
            const response = await api.get('/reporting/product-intelligence', { params });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getOperationalEfficiency: async (month = null, tenantId = null) => {
        try {
            const params = {};
            if (month && month !== 'all-time') params.month = month;
            if (tenantId) params.tenant_id = tenantId;
            const response = await api.get('/reporting/operational-efficiency', { params });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getCostManagement: async (month = null, tenantId = null) => {
        try {
            const params = {};
            if (month && month !== 'all-time') params.month = month;
            if (tenantId) params.tenant_id = tenantId;
            const response = await api.get('/reporting/cost-management', { params });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getCustomerBehavior: async (month = null) => {
        try {
            const params = month && month !== 'all-time' ? { month } : {};
            const response = await api.get('/reporting/customer-behavior', { params });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getPredictiveAnalytics: async (month = null) => {
        try {
            const params = month && month !== 'all-time' ? { month } : {};
            const response = await api.get('/reporting/predictive-analytics', { params });
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    getDailyAnalysis: async (month = null) => {
        try {
            const params = month && month !== 'all-time' ? { month } : {};
            const response = await api.get('/reporting/financial-performance/daily-analysis', { params });
            return response.data;
        } catch (error) {
            throw error;
        }
    },
    getDailyDetail: async (date, tenantId = null) => {
        try {
            const params = {};
            if (tenantId) params.tenant_id = tenantId;
            const response = await api.get(`/reporting/financial-performance/daily-detail/${date}`, { params });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getIncomePivot: async (month = null, aggregation = 'monthly', group1 = 'payment_method', group2 = 'dining_option', tenantId = null) => {
        try {
            const params = {};
            if (month && month !== 'all-time') params.month = month;
            params.aggregation = aggregation;
            params.group1 = group1;
            params.group2 = group2;
            if (tenantId) params.tenant_id = tenantId;
            const response = await api.get('/reporting/financial-performance/income-pivot', { params });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getExpensePivot: async (month = null, aggregation = 'monthly', group1 = 'category', group2 = 'product', tenantId = null) => {
        try {
            const params = {};
            if (month && month !== 'all-time') params.month = month;
            params.aggregation = aggregation;
            params.group1 = group1;
            params.group2 = group2;
            if (tenantId) params.tenant_id = tenantId;
            const response = await api.get('/reporting/financial-performance/expense-pivot', { params });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getDailyAnalysisWithDateRange: async (month = null, startDate = null, endDate = null, tenantId = null) => {
        try {
            const params = {};
            if (startDate && endDate) {
                params.start_date = startDate;
                params.end_date = endDate;
            } else if (month && month !== 'all-time') {
                params.month = month;
            }
            if (tenantId) params.tenant_id = tenantId;
            const response = await api.get('/reporting/financial-performance/daily-analysis', { params }); return response.data;
        } catch (error) {
            throw error;
        }
    },

    getPortalSummary: async (startDate, endDate) => {
        const response = await api.get('/reporting/portal-summary', {
            params: { start_date: startDate, end_date: endDate }
        });
        return response.data;
    },

    // ─── Reporting v2 ─────────────────────────────────────────────────
    getFinancialV2: async (startDate, endDate) => {
        const response = await api.get('/reporting/v2/financial', {
            params: { start_date: startDate, end_date: endDate },
        });
        return response.data;
    },

    getProductsV2: async (startDate, endDate) => {
        const response = await api.get('/reporting/v2/products', {
            params: { start_date: startDate, end_date: endDate },
        });
        return response.data;
    },

    getProductNarrative: async (startDate, endDate) => {
        const response = await api.get('/reporting/v2/narrative/products', {
            params: { start_date: startDate, end_date: endDate },
        });
        return response.data;
    },

    getProductDetail: async (productCode, startDate, endDate) => {
        const response = await api.get(`/reporting/v2/products/${encodeURIComponent(productCode)}`, {
            params: { start_date: startDate, end_date: endDate },
        });
        return response.data;
    },

    getCostsV2: async (startDate, endDate) => {
        const response = await api.get('/reporting/v2/costs', {
            params: { start_date: startDate, end_date: endDate },
        });
        return response.data;
    },

    getOperationsV2: async (startDate, endDate) => {
        const response = await api.get('/reporting/v2/operations', {
            params: { start_date: startDate, end_date: endDate },
        });
        return response.data;
    },

    // ─── Narrative & AI ──────────────────────────────────────────────────
    getNarrativeV2: async (startDate, endDate) => {
        const response = await api.get('/reporting/v2/narrative', {
            params: { start_date: startDate, end_date: endDate },
        });
        return response.data;
    },

    getCostNarrativeV2: async (startDate, endDate) => {
        const response = await api.get('/reporting/v2/narrative/costs', {
            params: { start_date: startDate, end_date: endDate },
        });
        return response.data;
    },

    // ─── Targets ─────────────────────────────────────────────────────────
    getTargetsV2: async () => {
        const response = await api.get('/reporting/v2/targets');
        return response.data;
    },

    createTargetV2: async (target) => {
        const response = await api.post('/reporting/v2/targets', target);
        return response.data;
    },

    deleteTargetV2: async (targetId) => {
        const response = await api.delete(`/reporting/v2/targets/${targetId}`);
        return response.data;
    },

    // Add these methods to reportingService object:
    getConversationStarters: () => api.get('/reporting/ai-analytics/conversation-starters'),
    getConversations: () => api.get('/reporting/ai-analytics/conversations'),
    getConversation: (sessionId) => api.get(`/reporting/ai-analytics/conversations/${sessionId}`),
    deleteConversation: (sessionId) => api.delete(`/reporting/ai-analytics/conversations/${sessionId}`),
    updateConversationTitle: (sessionId, title) => api.put(`/reporting/ai-analytics/conversations/${sessionId}/title`, { title }),
    toggleConversationPin: (sessionId) => api.put(`/reporting/ai-analytics/conversations/${sessionId}/pin`),
};

// Translation services
export const translationService = {
    getAllTranslations: async (category = null) => {
        try {
            const params = category ? `?category=${category}` : '';
            const response = await api.get(`/translations/${params}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getTranslation: async (englishText) => {
        try {
            const response = await api.get(`/translations/${encodeURIComponent(englishText)}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getTranslationsBatch: async (englishTexts) => {
        try {
            const response = await api.get('/translations/batch/by-texts', {
                params: { english_texts: englishTexts }
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    createTranslation: async (translationData) => {
        try {
            const response = await api.post('/translations/', translationData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    updateTranslation: async (englishText, updateData) => {
        try {
            const response = await api.put(`/translations/${encodeURIComponent(englishText)}`, updateData);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    syncProducts: async () => {
        try {
            const response = await api.post('/translations/sync-products');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    translateProductName: async (text, category = 'product') => {
        try {
            const response = await api.post('/translations/translate', { text, category });
            return response.data;
        } catch (error) {
            console.error('Translation request failed:', error);
            return { translation: null, transliteration: null, community_manuals: [], suggestion: null };
        }
    },
};

// Public translation services (no authentication required)
export const publicTranslationService = {
    getAllTranslations: async (category = null) => {
        try {
            // Create axios instance without auth interceptor for public calls
            const publicApi = axios.create({
                baseURL: API_URL,
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const response = await publicApi.get(`/public-translations`);
            return response.data;
        } catch (error) {
            console.error('Failed to load public translations:', error);
            return [];
        }
    }
};
// Super-admin services
export const adminService = {
    // Companies
    getCompanies: async () => {
        const response = await api.get('/admin/companies');
        return response.data;
    },
    createCompany: async (data) => {
        const response = await api.post('/admin/companies', data);
        return response.data;
    },
    updateCompany: async (id, data) => {
        const response = await api.put(`/admin/companies/${id}`, data);
        return response.data;
    },
    updateCompanyStatus: async (id, isActive) => {
        const response = await api.put(`/admin/companies/${id}/status`, { is_active: isActive });
        return response.data;
    },
    deleteCompany: async (id) => {
        await api.delete(`/admin/companies/${id}`);
    },

    // Tenants
    getTenants: async () => {
        const response = await api.get('/admin/tenants');
        return response.data;
    },
    createTenant: async (data) => {
        const response = await api.post('/admin/tenants', data);
        return response.data;
    },
    updateTenant: async (id, data) => {
        const response = await api.put(`/admin/tenants/${id}`, data);
        return response.data;
    },
    updateTenantStatus: async (id, isActive) => {
        const response = await api.put(`/admin/tenants/${id}/status`, { is_active: isActive });
        return response.data;
    },
    uploadTenantLogo: async (id, file, logoType) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post(`/admin/tenants/${id}/logo?logo_type=${logoType}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    // Credentials
    getCredentials: async (tenantId) => {
        const response = await api.get(`/admin/credentials?tenant_id=${tenantId}`);
        return response.data;
    },
    createCredential: async (data) => {
        const response = await api.post('/admin/credentials', data);
        return response.data;
    },
    updateCredential: async (id, data) => {
        const response = await api.put(`/admin/credentials/${id}`, data);
        return response.data;
    },
    deleteCredential: async (id) => {
        const response = await api.delete(`/admin/credentials/${id}`);
        return response.data;
    },
    // Employees (tenant-scoped)
    getEmployees: async (tenantId) => {
        const response = await api.get(`/admin/employees?tenant_id=${tenantId}`);
        return response.data;
    },
    updateEmployee: async (employeeId, tenantId, data) => {
        const response = await api.put(`/admin/employees/${employeeId}?tenant_id=${tenantId}`, data);
        return response.data;
    },
};

// ─── Roles & Permissions ─────────────────────────────────────────────────────
export const roleService = {
    // Roles
    getRoles: async (tenantId) => {
        const response = await api.get(`/admin/roles?tenant_id=${tenantId}`);
        return response.data;
    },
    createRole: async (tenantId, data) => {
        const response = await api.post(`/admin/roles?tenant_id=${tenantId}`, data);
        return response.data;
    },
    updateRole: async (roleId, tenantId, data) => {
        const response = await api.put(`/admin/roles/${roleId}?tenant_id=${tenantId}`, data);
        return response.data;
    },
    deleteRole: async (roleId, tenantId) => {
        const response = await api.delete(`/admin/roles/${roleId}?tenant_id=${tenantId}`);
        return response.data;
    },

    // Permissions
    getPermissions: async () => {
        const response = await api.get('/admin/permissions');
        return response.data;
    },
    getRolePermissions: async (roleId, tenantId) => {
        const response = await api.get(`/admin/roles/${roleId}/permissions?tenant_id=${tenantId}`);
        return response.data;
    },
    updateRolePermissions: async (roleId, tenantId, permissions) => {
        const response = await api.put(`/admin/roles/${roleId}/permissions?tenant_id=${tenantId}`, { permissions });
        return response.data;
    },

    // Tenant-scoped credentials (accessible to tenant_admin, not just super_admin)
    getCredentials: async (tenantId) => {
        const response = await api.get(`/admin/tenant-credentials?tenant_id=${tenantId}`);
        return response.data;
    },
    createCredential: async (tenantId, data) => {
        const response = await api.post(`/admin/tenant-credentials?tenant_id=${tenantId}`, data);
        return response.data;
    },
    updateCredential: async (credId, tenantId, data) => {
        const response = await api.put(`/admin/tenant-credentials/${credId}?tenant_id=${tenantId}`, data);
        return response.data;
    },
    resetPassword: async (credId, tenantId, newPassword) => {
        const response = await api.put(`/admin/tenant-credentials/${credId}/reset-password?tenant_id=${tenantId}`, { new_password: newPassword });
        return response.data;
    },
};

// Session / End-of-Day services
export const sessionService = {
    createSession: async (data) => {
        const response = await api.post('/sessions/', data);
        return response.data;
    },
    getActiveSessions: async () => {
        const response = await api.get('/sessions/active');
        return response.data;
    },
    getSession: async (sessionId) => {
        const response = await api.get(`/sessions/${sessionId}`);
        return response.data;
    },
    getSessionsByDate: async (salesDate) => {
        const response = await api.get(`/sessions/date/${encodeURIComponent(salesDate)}`);
        return response.data;
    },
    closeSession: async (sessionId, data) => {
        const response = await api.put(`/sessions/${sessionId}/close`, data);
        return response.data;
    },
    forceCloseSession: async (sessionId, data) => {
        const response = await api.put(`/sessions/${sessionId}/force-close`, data);
        return response.data;
    },
    getSessionSummary: async (sessionId) => {
        const response = await api.get(`/sessions/${sessionId}/summary`);
        return response.data;
    },
    getStoreSummary: async (salesDate) => {
        const response = await api.get(`/sessions/store-summary/${encodeURIComponent(salesDate)}`);
        return response.data;
    },
    getBusinessDates: async () => {
        const response = await api.get('/sessions/business-dates');
        return Array.isArray(response.data) ? response.data : [];
    },
};

export const eodReportService = {
    // Full EOD payload for one business day.
    getReport: async (salesDate) => {
        const response = await api.get(`/end-of-day/report/${encodeURIComponent(salesDate)}`);
        return response.data;
    },
    // Most recent CLOSED business day — never a day still being traded. The
    // endpoint answers 204 when the tenant has no closed day yet, which axios
    // surfaces as an empty-string body.
    getLastClosedReport: async () => {
        const response = await api.get('/end-of-day/report/last-closed');
        return response.status === 204 || !response.data ? null : response.data;
    },
    // Thermal payload: {printer_id, job_id, ip_address, port, xml}.
    // Carries the same explicit timeout as getReceiptPayloads/getEposTicketPayload:
    // the shared axios client has no default timeout (axios' own default waits
    // forever), and the backend cold-starts. Without it, an unresponsive
    // backend leaves the Export button stuck on "Working…" with no way out.
    getEodPrintPayload: async (salesDate, printerId) => {
        const response = await api.get('/printing/epson/eod-payload', {
            params: {
                sales_date: salesDate,
                ...(printerId ? { printer_id: printerId } : {}),
            },
            timeout: EPOS_PAYLOAD_TIMEOUT_MS,
        });
        return response.data;
    },
};

export const voidService = {
    getVoids: async (params = {}) => {
        const response = await api.get('/voids/', { params });
        return response.data;
    },
};

export const deviceService = {
    listDevices: async () => {
        const response = await api.get('/devices/');
        return response.data;
    },
    revokeDevice: async (deviceId) => {
        const response = await api.delete(`/devices/${deviceId}`);
        return response.data;
    },
    getCurrentDevice: async () => {
        const response = await api.get('/devices/me');
        return response.data;
    },
};


// ─── People v2 — nested employee shape, payrun lifecycle, payslips, config ─

export const employeeV2Service = {
    listEmployees: async (params = {}) => {
        const response = await api.get('/employees/v2/list', { params });
        return response.data;
    },
    getEmployeeDetail: async (employeeId) => {
        const response = await api.get(`/employees/v2/${employeeId}`);
        return response.data;
    },
    createEmployee: async (payload) => {
        const response = await api.post('/employees/v2/', payload);
        return response.data;
    },
    updateCore: async (employeeId, payload) => {
        const response = await api.patch(`/employees/v2/${employeeId}`, payload);
        return response.data;
    },
    updatePersonal: async (employeeId, payload) => {
        const response = await api.patch(`/employees/v2/${employeeId}/personal`, payload);
        return response.data;
    },
    updateEmployment: async (employeeId, payload) => {
        const response = await api.patch(`/employees/v2/${employeeId}/employment`, payload);
        return response.data;
    },
    updateCompensation: async (employeeId, payload) => {
        const response = await api.patch(`/employees/v2/${employeeId}/compensation`, payload);
        return response.data;
    },
    updateAccess: async (employeeId, payload) => {
        const response = await api.patch(`/employees/v2/${employeeId}/access`, payload);
        return response.data;
    },
    softDelete: async (employeeId, reason = null) => {
        const params = reason ? { reason } : {};
        const response = await api.delete(`/employees/v2/${employeeId}`, { params });
        return response.data;
    },
    getCompensationHistory: async (employeeId) => {
        const response = await api.get(`/employees/v2/${employeeId}/compensation-history`);
        return response.data;
    },
    listDocuments: async (employeeId) => {
        const response = await api.get(`/employees/v2/${employeeId}/documents`);
        return response.data;
    },
    uploadDocument: async (employeeId, file, documentType) => {
        const form = new FormData();
        form.append('file', file);
        form.append('document_type', documentType);
        const response = await api.post(`/employees/v2/${employeeId}/documents`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },
    getDocumentSignedUrl: async (employeeId, documentId) => {
        const response = await api.get(`/employees/v2/${employeeId}/documents/${documentId}`);
        return response.data;
    },
    deleteDocument: async (employeeId, documentId) => {
        const response = await api.delete(`/employees/v2/${employeeId}/documents/${documentId}`);
        return response.data;
    },
    listBonuses: async (employeeId) => {
        const response = await api.get(`/employees/v2/${employeeId}/bonuses`);
        return response.data;
    },
    createBonus: async (employeeId, payload) => {
        const response = await api.post(`/employees/v2/${employeeId}/bonuses`, payload);
        return response.data;
    },
    listPayslips: async (employeeId) => {
        const response = await api.get(`/employees/v2/${employeeId}/payslips`);
        return response.data;
    },
};

export const payrunService = {
    list: async (params = {}) => {
        // FastAPI accepts the trailing slash in the prefixed router.
        const response = await api.get('/payrun/', { params });
        return response.data;
    },
    get: async (payRunId) => {
        const response = await api.get(`/payrun/${payRunId}`);
        return response.data;
    },
    createDraft: async (payload) => {
        const response = await api.post('/payrun/', payload);
        return response.data;
    },
    deleteDraft: async (payRunId) => {
        const response = await api.delete(`/payrun/${payRunId}`);
        return response.data;
    },
    refreshDraft: async (payRunId) => {
        const response = await api.post(`/payrun/${payRunId}/refresh`);
        return response.data;
    },
    finalize: async (payRunId) => {
        const response = await api.post(`/payrun/${payRunId}/finalize`);
        return response.data;
    },
    reFinalize: async (payRunId, reason = null) => {
        const response = await api.post(`/payrun/${payRunId}/re_finalize`, { reason });
        return response.data;
    },
    close: async (payRunId) => {
        const response = await api.post(`/payrun/${payRunId}/close`);
        return response.data;
    },
    reopen: async (payRunId, reason) => {
        const response = await api.post(`/payrun/${payRunId}/reopen`, { reason });
        return response.data;
    },
    recordPayments: async (payRunId, payments) => {
        const response = await api.post(`/payrun/${payRunId}/record_payments`, { payments });
        return response.data;
    },
    recordSinglePayment: async (payRunId, lineItemId, payload) => {
        const response = await api.post(
            `/payrun/${payRunId}/line_items/${lineItemId}/payments`,
            payload,
        );
        return response.data;
    },
    updateLineItem: async (payRunId, lineItemId, payload) => {
        const response = await api.patch(
            `/payrun/${payRunId}/line_items/${lineItemId}`,
            payload,
        );
        return response.data;
    },
    exportCsvUrl: (payRunId) => `${API_URL}/payrun/${payRunId}/export.csv`,
};

export const payslipService = {
    get: async (payslipId) => {
        const response = await api.get(`/payslips/${payslipId}`);
        return response.data;
    },
    getPdfPayload: async (payslipId) => {
        const response = await api.get(`/payslips/${payslipId}/pdf`);
        return response.data;
    },
    regenerateForLineItem: async (lineItemId) => {
        const response = await api.post(`/line_items/${lineItemId}/regenerate_payslip`);
        return response.data;
    },
};

export const payrollConfigService = {
    get: async () => {
        const response = await api.get('/tenant/payroll-config');
        return response.data;
    },
    update: async (payload) => {
        const response = await api.patch('/tenant/payroll-config', payload);
        return response.data;
    },
};

export const dashboardService = {
    getStats: async () => {
        const response = await api.get('/portal/dashboard-stats');
        return response.data;
    },
    refreshAnalytics: async () => {
        const response = await api.post('/portal/refresh-analytics');
        return response.data;
    },
};

export const helpCenterService = {
    chat: async (message, conversationHistory = []) => {
        const response = await api.post('/api/help-center/chat', {
            message,
            conversation_history: conversationHistory,
        });
        return response.data;
    },
    createTicket: async (summary, chatTranscript) => {
        const response = await api.post('/api/help-center/tickets', {
            summary,
            chat_transcript: chatTranscript,
        });
        return response.data;
    },
    getTickets: async (status = null, tenantId = null) => {
        const params = {};
        if (status) params.status = status;
        if (tenantId) params.tenant_id = tenantId;
        const response = await api.get('/api/help-center/tickets', { params });
        return response.data;
    },
    updateTicket: async (ticketId, status) => {
        const response = await api.patch(`/api/help-center/tickets/${ticketId}`, { status });
        return response.data;
    },
};

export const guestService = {
    list: async (search = '') => {
        const params = search ? `?search=${encodeURIComponent(search)}` : '';
        const response = await api.get(`/guests/${params}`);
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/guests/', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.patch(`/guests/${id}`, data);
        return response.data;
    },
};

export const reservationService = {
    list: async (params = {}) => {
        const query = new URLSearchParams(params).toString();
        const response = await api.get(`/reservations/?${query}`);
        return response.data;
    },
    upcoming: async (hours = 2) => {
        const response = await api.get(`/reservations/upcoming?hours=${hours}`);
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/reservations/', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.patch(`/reservations/${id}`, data);
        return response.data;
    },
    seat: async (id, data) => {
        const response = await api.post(`/reservations/${id}/seat`, data);
        return response.data;
    },
    cancel: async (id) => {
        const response = await api.post(`/reservations/${id}/cancel`);
        return response.data;
    },
    noShow: async (id) => {
        const response = await api.post(`/reservations/${id}/no-show`);
        return response.data;
    },
};

export const waitingListService = {
    list: async (params = {}) => {
        const query = new URLSearchParams(params).toString();
        const response = await api.get(`/waiting-list/?${query}`);
        return response.data;
    },
    add: async (data) => {
        const response = await api.post('/waiting-list/', data);
        return response.data;
    },
    update: async (id, data) => {
        const response = await api.patch(`/waiting-list/${id}`, data);
        return response.data;
    },
    notify: async (id) => {
        const response = await api.post(`/waiting-list/${id}/notify`);
        return response.data;
    },
    seat: async (id, data) => {
        const response = await api.post(`/waiting-list/${id}/seat`, data);
        return response.data;
    },
    leave: async (id) => {
        const response = await api.post(`/waiting-list/${id}/leave`);
        return response.data;
    },
    noShow: async (id) => {
        const response = await api.post(`/waiting-list/${id}/no-show`);
        return response.data;
    },
    reorder: async (entryIds) => {
        const response = await api.post('/waiting-list/reorder', { entry_ids: entryIds });
        return response.data;
    },
};

export const catalogueOrderService = {
    // ONE call carrying the whole new order. Per-item updates would leave the
    // menu half-reordered if the till's connection drops mid-drag.
    // entity: 'products' | 'categories' | 'subcategories' | 'dining-options'
    reorder: async (entity, ids) => {
        const response = await api.post(`/catalogue-order/${entity}`, { ids });
        return response.data;
    },
};

export const managerApprovalService = {
    // Verifies a manager credential and records the audit row server-side.
    // Returns the approver; deliberately never returns or stores a token, so
    // an approval can never replace the device's operator session.
    verify: async ({ tenantId, pin, action, context = null, requestedByEmployeeId = null, salesDate = null }) => {
        const response = await api.post('/manager-approvals/verify', {
            tenant_id: tenantId,
            pin,
            action,
            context,
            requested_by_employee_id: requestedByEmployeeId,
            sales_date: salesDate,
        });
        return response.data;
    },
};

export const occupationService = {
    listActive: async (diningOptionId = null) => {
        const params = diningOptionId ? `?dining_option_id=${diningOptionId}` : '';
        const response = await api.get(`/occupations/active${params}`);
        return response.data;
    },
    getByTable: async (diningOptionId, tableNumber) => {
        const response = await api.get(`/occupations/by-table/${diningOptionId}/${tableNumber}`);
        return response.data;
    },
    create: async (data) => {
        const response = await api.post('/occupations/', data);
        return response.data;
    },
    overrideTimestamps: async (id, data) => {
        const response = await api.patch(`/occupations/${id}`, data);
        return response.data;
    },
    clearTable: async (id) => {
        const response = await api.post(`/occupations/${id}/clear`);
        return response.data;
    },
    requestBill: async (id) => {
        const response = await api.post(`/occupations/${id}/request-bill`);
        return response.data;
    },
    markFirstOrder: async (id) => {
        const response = await api.post(`/occupations/${id}/mark-first-order`);
        return response.data;
    },
    markServed: async (id) => {
        const response = await api.post(`/occupations/${id}/mark-served`);
        return response.data;
    },
    changeTable: async (id, tableNumber) => {
        const response = await api.post(`/occupations/${id}/change-table`, { table_number: tableNumber });
        return response.data;
    },
    merge: async (payload) => {
        const response = await api.post('/occupations/merge', payload);
        return response.data;
    },
    split: async (id) => {
        const response = await api.post(`/occupations/${id}/split`);
        return response.data;
    },
};

export const marketingService = {
    getConnections: async () => {
        const res = await api.get('/api/marketing/connections');
        return res.data;
    },
    getMetaAuthorizeUrl: async () => {
        const res = await api.get('/api/marketing/connections/meta/authorize');
        return res.data.url;
    },
    disconnect: async (platform, externalAccountId) => {
        const res = await api.delete(`/api/marketing/connections/${platform}/${externalAccountId}`);
        return res.data;
    },
    getInsights: async (startDate, endDate) => {
        const res = await api.get('/api/marketing/insights', { params: { start_date: startDate, end_date: endDate } });
        return res.data;
    },
    getTopPosts: async (startDate, endDate, platform = null, limit = 10) => {
        const res = await api.get('/api/marketing/insights/posts', { params: { start_date: startDate, end_date: endDate, platform, limit } });
        return res.data;
    },
    refresh: async () => {
        const res = await api.post('/api/marketing/refresh');
        return res.data;
    },
    getRefreshStatus: async () => {
        const res = await api.get('/api/marketing/refresh/status');
        return res.data;
    },
    getGoogleAuthorizeUrl: async () => {
        const res = await api.get('/api/marketing/connections/google/authorize');
        return res.data.url;
    },
    getGoogleLocations: async () => {
        const res = await api.get('/api/marketing/connections/google/locations');
        return res.data;
    },
    selectGoogleLocation: async (locationId, accountId, displayName) => {
        const res = await api.post('/api/marketing/connections/google/select-location', {
            location_id: locationId, account_id: accountId, display_name: displayName,
        });
        return res.data;
    },
    getLocalDiscovery: async (startDate, endDate) => {
        const res = await api.get('/api/marketing/insights/local-discovery', { params: { start_date: startDate, end_date: endDate } });
        return res.data;
    },
    getReviews: async (limit = 50, offset = 0) => {
        const res = await api.get('/api/marketing/insights/reviews', { params: { limit, offset } });
        return res.data;
    },
    getSearchKeywords: async (month = null, limit = 25) => {
        const res = await api.get('/api/marketing/insights/search-keywords', { params: { month, limit } });
        return res.data;
    },
};

export const promotionsService = {
    getAll: async () => (await api.get('/api/promotions/')).data,
    create: async (data) => (await api.post('/api/promotions/', data)).data,
    update: async (id, data) => (await api.put(`/api/promotions/${id}`, data)).data,
    delete: async (id) => (await api.delete(`/api/promotions/${id}`)).data,
    // POS-facing loader: backend, enabled only.
    // The backend already returns rule_config/scope_ids/days_of_week as native JSON
    // and enabled as a bool, matching the shape the promotion engine consumes.
    getActiveForPos: async () => {
        const all = (await api.get('/api/promotions/')).data;
        return (all || []).filter(p => p.enabled);
    },
};

// Whole-branch review, finding 1(a): how long to wait on a rendered ePOS
// payload before giving up. The shared axios client above is created with
// no `timeout`, and axios' own default is 0 — wait forever — so without
// this an unanswered payload request during a backend cold start hangs its
// caller for the whole connection lifetime with no cancel. That call sits
// on the post-payment path (attemptDirectEpsonPrint), which is exactly the
// hazard LIST_PRINTERS_TIMEOUT_MS (PrintingUtils.js) already guards on the
// printer lookup; this is the same guard for the fetch that follows it.
//
// Set here rather than passed per call: attemptDirectEpsonPrint invokes
// these with TWO arguments when the device has no printer assignment (the
// "unassigned device's request stays byte-identical" contract pinned in
// devicePrinterResolution.test.js), so a trailing config argument would
// change that call's arity. A default on the service can't be forgotten by
// a future caller either.
//
// Generous enough (10s) to clear a Cloud Run cold start rather than lose a
// print that would have succeeded; giving up is safe either way — the
// backend already queued this print's job (enqueue_receipt_for_sale /
// enqueue_ticket_for_order), so nothing is lost, and the caller treats a
// timeout exactly like any other fetch failure.
export const EPOS_PAYLOAD_TIMEOUT_MS = 10000;

export const printingService = {
    // `config` is a plain axios request config, used by PrintingUtils to put
    // a short per-call `timeout` on the pre-print printer lookup without
    // changing the shared client's defaults for every other endpoint.
    listPrinters: async (config = {}) => (await api.get('/printing/printers', config)).data,
    // `opts` carries the fields only network-direct printers need
    // ({ protocol, ip_address, port }) — omitted entirely for the default
    // CloudPRNT flow so existing callers (and the request payload) are
    // unchanged.
    createPrinter: async (name, role, opts = {}) => (await api.post('/printing/printers', { name, role, ...opts })).data,
    deletePrinter: async (printerId) => { await api.delete(`/printing/printers/${printerId}`); },
    testPrint: async (printerId) => (await api.post(`/printing/printers/${printerId}/test`)).data,
    recentJobs: async (limit = 10, status = null) =>
        (await api.get('/printing/jobs', { params: { limit, ...(status ? { status } : {}) } })).data,
    // Full URL a Star CloudPRNT printer polls — composed from the same API base
    // the axios client uses, so it is correct per environment (local/staging/prod).
    getCloudprntUrl: (token) => `${API_URL}/printing/cloudprnt/${token}`,
    // Device-side Epson direct print (F-epson Task 6): fetch the rendered
    // ePOS-Print payload + target address for one transaction/role, then
    // report the outcome of POSTing it to the printer. Every payload fetch
    // enqueues a new PrintJob backend-side — call once per print attempt,
    // never speculatively or in a retry loop.
    // `printerId` (Device Printer Config Task 3/4) is optional: only present
    // when this device has an assigned printer for the print. It is omitted
    // from the params object entirely when absent — not sent as a literal
    // `printer_id: null`/`undefined` — matching the backend's optional
    // query-param contract and keeping an unassigned device's request
    // byte-identical to the pre-Task-3 call.
    //
    // Returns EVERY copy the sale should print — the customer's receipt plus
    // a cashier copy per payment method — as `{ payloads: [...] }`. It used
    // to return one payload, which is why a split printed a single receipt
    // on the app while the browser produced three: the app had no way to
    // ask for the rest. There is no `role`; this endpoint only ever renders
    // receipts.
    // `language` carries the cashier's "Receipt Translations" toggle. It is
    // a device setting, so it has to travel with the request — the renderer
    // is server-side and cannot see it. Omitted entirely when off, keeping
    // the common call byte-identical and costing the backend no lookup.
    // `printedAt` is this device's LOCAL print moment, "YYYY-MM-DD HH:MM:SS"
    // (utils/printedAtStamp.js). Like `language` it has to travel with the
    // request, and for a harder reason: the renderer is server-side and every
    // clock the backend owns is UTC (`datetime.utcnow`), with no tenant
    // timezone stored anywhere to convert with. It stamped one anyway once,
    // and a staging receipt came out carrying `Time: 23:07:36` in its header
    // and `Printed: ... 22:07:42` in its footer — one sale, two clocks, an
    // hour apart under BST. The backend now stamps only what it is handed.
    // Omitted entirely when absent, same as the other optional params.
    getReceiptPayloads: async (transactionId, printerId, language, printedAt) =>
        (await api.get('/printing/receipt-payloads', {
            params: {
                transaction_id: transactionId,
                ...(printerId ? { printer_id: printerId } : {}),
                ...(language ? { language } : {}),
                ...(printedAt ? { printed_at: printedAt } : {}),
            },
            timeout: EPOS_PAYLOAD_TIMEOUT_MS,
        })).data,
    // The same copies as plain text, for the browser to print. Same
    // renderer, so the two surfaces cannot drift apart again — the browser
    // used to build its own HTML, which is how they did. `printedAt` as
    // above: the browser is a device with a local clock too, and omitting it
    // here would take the `Printed:` line off browser receipts only.
    getReceiptText: async (transactionId, language, printedAt) =>
        (await api.get('/printing/receipt-text', {
            params: {
                transaction_id: transactionId,
                ...(language ? { language } : {}),
                ...(printedAt ? { printed_at: printedAt } : {}),
            },
            timeout: EPOS_PAYLOAD_TIMEOUT_MS,
        })).data,
    // Device Printer Config Task 8: ticket-side sibling of getReceiptPayloads —
    // a kitchen/bar ticket fires when an order is sent to the kitchen, not
    // when it is paid, so it is keyed by `tracking_id` (an OrderTracking
    // record) instead of `transaction_id`. Same shape/contract otherwise:
    // one PrintJob enqueued per fetch, `printerId` optional and omitted
    // entirely (not sent as a literal null/undefined) when this device has
    // no per-role assignment.
    // `language` and `printedAt` are additive and omitted entirely when absent,
    // exactly like `printerId` — a backend that predates them ignores what it
    // never receives. `language` closes item 18 (kitchen tickets printed no
    // translations while the customer receipt did); `printedAt` is the only
    // local clock a ticket has, since OrderTracking.submitted_at is UTC.
    getEposTicketPayload: async (role, trackingId, printerId, language, printedAt) =>
        (await api.get('/printing/epson/ticket-payload', {
            params: {
                role,
                tracking_id: trackingId,
                ...(printerId ? { printer_id: printerId } : {}),
                ...(language ? { language } : {}),
                ...(printedAt ? { printed_at: printedAt } : {}),
            },
            timeout: EPOS_PAYLOAD_TIMEOUT_MS,
        })).data,
    // Device Printer Config Task 3/4: fetch the rendered ePOS-Print payload
    // for a manual test page (no transaction, no job tracking) so the
    // station settings sheet can POST it straight to the printer via
    // eposClient.sendToPrinter, mirroring the payload shape minus
    // `job_id`.
    getEposTestPayload: async (printerId) =>
        (await api.get('/printing/epson/test-payload', { params: { printer_id: printerId } })).data,
    // Customer refund confirmation. Same contract as getReceiptPayloads — one
    // PrintJob enqueued per fetch, `printerId` omitted entirely (never sent
    // as a literal null/undefined) when this device has no assignment.
    //
    // `printedAt` DEFAULTS to this device's local clock rather than being
    // required of the caller, which is the one place this differs from
    // getReceiptPayloads. The slip's `Date:`/`Time:` is the refund moment and
    // there is nowhere else to get it: `Refund.created_at` is
    // `datetime.utcnow` and the backend has no tenant timezone to convert it
    // with, so it now stamps only what it is handed — and a caller that
    // forgot to hand it one would silently print a refund slip with no
    // moment on it at all. Overridable so a caller that knows the exact
    // instant it pressed print can pass it (utils/printedAtStamp.js).
    // `language` is FOURTH, after the defaulted `printedAt`, rather than in
    // the third slot the receipt and ticket calls put it in. Deliberate: the
    // three-argument shape already has callers and tests pinning it, and
    // reordering to match the siblings would break them for a cosmetic win.
    // Omitted or null sends no `language` param at all, so the request is
    // byte-identical to what it was before.
    getRefundPayload: async (refundId, printerId, printedAt = printedAtStamp(), language) =>
        (await api.get('/printing/epson/refund-payload', {
            params: {
                refund_id: refundId,
                ...(printerId ? { printer_id: printerId } : {}),
                ...(printedAt ? { printed_at: printedAt } : {}),
                ...(language ? { language } : {}),
            },
            timeout: EPOS_PAYLOAD_TIMEOUT_MS,
        })).data,
    reportJobResult: async (jobId, body) =>
        (await api.post(`/printing/jobs/${jobId}/result`, body)).data,
    // Saves what the on-device probe detected. Protocol only — the endpoint
    // deliberately refuses to touch anything else about the printer.
    setPrinterProtocol: async (printerId, protocol) =>
        (await api.patch(`/printing/printers/${printerId}`, { protocol })).data,
    // Cash Drawer Kick Task 3/4: marks a printer as having a drawer wired to
    // its DK port. Drawer-only — deliberately narrower than
    // setPrinterProtocol above.
    setPrinterDrawer: async (printerId, hasDrawer) =>
        (await api.patch(`/printing/printers/${printerId}/drawer`,
            { has_drawer: hasDrawer })).data,
};

// Cash Drawer Kick: fetches the drawer-pulse payload a printer needs to fire
// its solenoid. Two ways in — automatic (tied to a completed sale, gated by
// the shop's drawer policy) and manual (gated by a single-use manager
// approval) — both handed to the same transport (utils/drawerKick.js) since
// the payload shape is identical either way.
export const drawerService = {
    // 204 means "this sale does not open the drawer" — a normal outcome, not
    // an error, so it resolves to null rather than throwing.
    payloadForSale: async (transactionId, printerId) => {
        const params = { transaction_id: transactionId };
        if (printerId) params.printer_id = printerId;
        const response = await api.get('/printing/drawer-payload', { params });
        return response.status === 204 || !response.data ? null : response.data;
    },
    openManually: async (body) => (await api.post('/printing/drawer-open', body)).data,
};

// Refunds. Portal and both tills use these; there is deliberately no
// local-DB branch. A refund needs the server's view of what is still
// refundable — two tills can refund the same sale at once, and an offline
// copy would let both hand back the full amount.
export const refundService = {
    create: async (payload) => (await api.post('/refunds', payload)).data,
    forTransaction: async (transactionId) =>
        (await api.get(`/refunds/transaction/${transactionId}`)).data,
    forSalesDate: async (salesDate) =>
        (await api.get(`/refunds/sales-date/${encodeURIComponent(salesDate)}`)).data,
    // The FULL sales_date strings ("2026-07-31 02:47:56"), most recent
    // first. A business day is identified by that whole value, not by a
    // calendar date — two can be opened on one date.
    businessDays: async () => (await api.get('/refunds/business-days')).data,
};

// Bulk recipe import/export. Portal-only (the Products and Inventory pages),
// so there is no local-DB branch — the POS never uploads recipe files.
export const recipeImportService = {
    // Resolves names, validates units and diffs against current recipes.
    // Writes nothing.
    preview: async (rows) =>
        (await api.post('/inventory/recipes/preview', { rows })).data,
    // Writes new and changed products in one transaction. Rejects the whole
    // file if anything fails to resolve — preview must be clean first.
    apply: async (rows) =>
        (await api.post('/inventory/recipes/apply', { rows })).data,
    export: async () =>
        (await api.get('/inventory/recipes/export')).data,
    // Valid names to write in a recipe file. Export only covers products
    // that already have a recipe, so a tenant starting from zero needs this.
    reference: async () =>
        (await api.get('/inventory/recipes/reference')).data,
};

export default api;